import { AnchorItem } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/types';
import { useCallback, useRef } from 'react';
import {
  CHILD_ITEM_OFFSET,
  CLICK_DISABLE_DURATION,
  SCROLL_OFFSET,
} from './constants';
import { isChildItem, updateUrlHash } from './utils';

export const useAnchorClick = (
  contentWrapperRef: React.RefObject<HTMLDivElement>,
  isClickingRef: React.MutableRefObject<boolean>,
  setCurrentAnchor: (anchor: string) => void,
  anchorItems: AnchorItem[]
) => {
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle anchor click - scroll to target
  const handleAnchorClick = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      link: { title: React.ReactNode; href: string }
    ) => {
      e.preventDefault();

      const targetElement = contentWrapperRef.current?.querySelector(link.href);
      if (!(targetElement instanceof HTMLElement)) {
        return;
      }

      // Clear any pending timeout from previous click
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }

      isClickingRef.current = true;
      updateUrlHash(link.href);
      setCurrentAnchor(link.href);

      const drawerBody = contentWrapperRef.current?.closest('.ant-drawer-body');
      if (!(drawerBody instanceof HTMLElement)) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clickTimeoutRef.current = setTimeout(() => {
          isClickingRef.current = false;
        }, CLICK_DISABLE_DURATION);
        return;
      }

      // Use offsetTop for absolute position (not affected by current scroll animation)
      const containerStyles = window.getComputedStyle(drawerBody);
      const containerPaddingTop = parseFloat(containerStyles.paddingTop) || 0;
      const childOffset = isChildItem(anchorItems, link.href)
        ? CHILD_ITEM_OFFSET
        : 0;

      // Calculate target scroll position from element's absolute offset
      const targetScrollTop =
        targetElement.offsetTop -
        containerPaddingTop -
        SCROLL_OFFSET +
        childOffset;
      const maxScrollTop = drawerBody.scrollHeight - drawerBody.clientHeight;
      const finalScrollTop = Math.min(
        Math.max(0, targetScrollTop),
        maxScrollTop
      );

      // Always smooth - position is correct regardless of current animation
      drawerBody.scrollTo({
        top: finalScrollTop,
        behavior: 'smooth',
      });

      clickTimeoutRef.current = setTimeout(() => {
        isClickingRef.current = false;
      }, CLICK_DISABLE_DURATION);
    },
    [anchorItems, contentWrapperRef, isClickingRef, setCurrentAnchor]
  );

  return {
    handleAnchorClick,
  };
};
